import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useCatalog } from "../../catalog/CatalogContext";
import OrderModal from "../OrderModal/OrderModal";
import { buildProductSlug, Product } from "../../data/products";

const heroHighlights = [
  "Une selection elegante de sacs et de chaussures",
  "Des pieces choisies pour faire tourner les tetes",
  "Commande rapide et simple sur WhatsApp",
];

export default function Hero() {
  const { products } = useCatalog();
  const [index, setIndex] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const slides = useMemo(() => {
    const featured = products.filter((product) => product.featured);
    const result: Product[][] = [];

    for (let i = 0; i < featured.length; i += 2) {
      const first = featured[i];
      const second = featured[i + 1] ?? featured[0];
      if (first && second) {
        result.push([first, second]);
      }
    }

    return result.length ? result : [products.slice(0, 2)];
  }, [products]);

  const next = () => setIndex((prev) => (prev + 1) % slides.length);
  const prev = () => setIndex((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <>
      <section className="px-4 pb-10 pt-2 md:px-8 lg:px-10">
        <div className="catalog-grid mx-auto grid max-w-7xl gap-6 rounded-[38px] border border-[rgba(62,35,24,0.08)] bg-[rgba(255,250,244,0.52)] p-4 md:p-6 lg:grid-cols-[1.08fr_1.2fr] lg:p-8">
          <div className="surface-card rounded-[34px] p-6 md:p-8 lg:p-10">
            <p className="brand-eyebrow">Collection 2026</p>
            <h1 className="brand-display mt-5 max-w-xl text-5xl font-bold leading-[0.92] text-[#1f1410] md:text-7xl">
              La mode qui fait toute la difference.
            </h1>

            <p className="mt-6 max-w-xl text-base leading-8 text-[#725b4f]">
              Des sacs et des chaussures qui apportent du style, de l’allure
              et une vraie touche de charme a chaque tenue.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/products"
                className="cta-primary inline-flex items-center justify-center rounded-full px-7 py-4 text-xs font-semibold uppercase tracking-[0.32em] transition"
              >
                Decouvrir la boutique
              </Link>
              <Link
                to="/products/news"
                className="cta-secondary inline-flex items-center justify-center rounded-full px-7 py-4 text-xs font-semibold uppercase tracking-[0.32em] transition hover:bg-white"
              >
                Voir les nouveautes
              </Link>
            </div>

            <div className="mt-10 grid gap-4">
              {heroHighlights.map((item) => (
                <div
                  key={item}
                  className="rounded-[22px] border border-[rgba(62,35,24,0.08)] bg-white/70 px-4 py-4 text-sm leading-7 text-[#4f392f]"
                >
                  {item}
                </div>
              ))}
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              <div className="rounded-[24px] bg-[#241712] px-5 py-5 text-[#fff4ea]">
                <div className="brand-display text-3xl font-bold">24h</div>
                <p className="mt-2 text-xs uppercase tracking-[0.22em] text-[#e6c8b4]">
                  Reponse rapide
                </p>
              </div>
              <div className="rounded-[24px] bg-[#f0d9c5] px-5 py-5 text-[#3b2419]">
                <div className="brand-display text-3xl font-bold">+{products.length}</div>
                <p className="mt-2 text-xs uppercase tracking-[0.22em] text-[#7d5843]">
                  Modeles a decouvrir
                </p>
              </div>
              <div className="rounded-[24px] bg-white/78 px-5 py-5 text-[#3b2419]">
                <div className="brand-display text-3xl font-bold">100%</div>
                <p className="mt-2 text-xs uppercase tracking-[0.22em] text-[#7d5843]">
                  Style et elegance
                </p>
              </div>
            </div>
          </div>

          <div className="surface-panel overflow-hidden rounded-[34px] p-4 md:p-6">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <p className="brand-eyebrow">A la une</p>
                <h2 className="brand-display mt-2 text-2xl font-bold text-[#1f1410] md:text-4xl">
                  Les pieces qui attirent tous les regards.
                </h2>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={prev}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(62,35,24,0.12)] bg-white/70 text-lg text-[#2b1b15] transition hover:bg-white"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={next}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(62,35,24,0.12)] bg-white/70 text-lg text-[#2b1b15] transition hover:bg-white"
                >
                  ›
                </button>
              </div>
            </div>

            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {slides.map((slide, slideIndex) => (
                <div
                  key={slideIndex}
                  className="grid min-w-full gap-4 lg:grid-cols-[1.08fr_0.92fr]"
                >
                  {slide.map((product, productIndex) => {
                    const isLead = productIndex === 0;

                    return (
                    <article
                      key={productIndex}
                      className={`group relative flex flex-col justify-between overflow-hidden rounded-[30px] border border-[rgba(62,35,24,0.08)] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.92),rgba(241,223,207,0.92))] ${
                        isLead
                          ? "min-h-[470px] p-6 md:p-7"
                          : "min-h-[410px] p-5 md:mt-8 md:p-6"
                      }`}
                    >
                      <div className="absolute inset-x-0 top-0 h-24 bg-[linear-gradient(180deg,rgba(255,255,255,0.45),transparent)]" />

                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#9b7158]">
                            {product.category === "bags" ? "Sac tendance" : "Chaussure tendance"}
                          </p>
                          <h3
                            className={`brand-display mt-3 font-bold text-[#21140f] ${
                              isLead ? "text-3xl md:text-[2.5rem]" : "text-2xl md:text-3xl"
                            }`}
                          >
                            {product.title}
                          </h3>
                        </div>
                        <span className="rounded-full bg-white/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6f4f3f]">
                          {product.price}
                        </span>
                      </div>

                      <div
                        className={`relative flex flex-1 items-center justify-center ${
                          isLead ? "py-10 md:py-12" : "py-7 md:py-8"
                        }`}
                      >
                        <div
                          className={`absolute rounded-full bg-[rgba(189,111,71,0.13)] blur-3xl ${
                            isLead ? "h-72 w-72" : "h-56 w-56"
                          }`}
                        />
                        <img
                          src={product.image}
                          alt={product.title}
                          className={`relative z-10 max-w-full object-contain transition duration-700 group-hover:scale-105 group-hover:-rotate-2 ${
                            isLead ? "max-h-[300px] md:max-h-[340px]" : "max-h-[220px] md:max-h-[250px]"
                          }`}
                        />
                      </div>

                      <div className="flex flex-col gap-4">
                        <div className="rounded-[22px] bg-white/60 px-4 py-4">
                          <span className="text-sm leading-7 text-[#725c50]">
                            {product.description ??
                              "Une belle piece pour apporter du style a votre tenue."}
                          </span>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row">
                          <button
                            type="button"
                            onClick={() => setSelectedProduct(product)}
                            className="cta-primary rounded-full px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.28em] transition"
                          >
                            Commander
                          </button>
                          <Link
                            to={`/product/${buildProductSlug(product)}`}
                            className="cta-secondary rounded-full px-5 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.28em] transition hover:bg-white"
                          >
                            Voir
                          </Link>
                        </div>
                      </div>
                    </article>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <OrderModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </>
  );
}
